import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button, emitUnhandledError } from '@/ui'
import type { WhatsAppTemplateData } from '@jobilla/nexus-sdk'
import { CheckIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTemplateBodyText } from './template-body'

export type TemplateAutofillEditorProps = {
	template: WhatsAppTemplateData
	isSaving: boolean
	onSave: (map: Record<string, string> | null) => Promise<void>
	onCancel: () => void
}

const NO_AUTOFILL_VALUE = '__none__'

export function TemplateAutofillEditor({ template, isSaving, onSave, onCancel }: TemplateAutofillEditorProps) {
	const { t } = useTranslation('common')
	const positions = useMemo(
		() => Array.from({ length: template.parameterCount }, (_, i) => String(i + 1)),
		[template.parameterCount],
	)

	const [draft, setDraft] = useState<Record<string, string>>(() => {
		const initial: Record<string, string> = {}
		positions.forEach((p) => {
			initial[p] = template.parameterMap?.[p] ?? NO_AUTOFILL_VALUE
		})
		return initial
	})

	const bodyText = getTemplateBodyText(template)

	const handleSave = async () => {
		const cleaned: Record<string, string> = {}
		for (const [position, field] of Object.entries(draft)) {
			if (field !== NO_AUTOFILL_VALUE) {
				cleaned[position] = field
			}
		}

		try {
			await onSave(Object.keys(cleaned).length > 0 ? cleaned : null)
		} catch (err) {
			emitUnhandledError({
				title: t('whatsappSettings.autofillSaveErrorTitle'),
				description: err instanceof Error ? err.message : t('whatsappSettings.autofillSaveErrorDesc'),
				cause: err,
			})
		}
	}

	if (template.parameterCount === 0) {
		return (
			<div className="mt-3 p-3 rounded-lg bg-gray-50 text-sm text-gray-600">
				{t('whatsappSettings.autofillNoPlaceholders')}
			</div>
		)
	}

	return (
		<div className="mt-3 p-4 rounded-lg border border-gray-200 bg-gray-50/60 space-y-4">
			{bodyText && (
				<div>
					<div className="text-xs font-medium text-gray-500 mb-1">
						{t('whatsappSettings.templateBody')}
					</div>
					<div className="text-sm text-gray-700 whitespace-pre-wrap font-mono">{bodyText}</div>
				</div>
			)}

			<div className="space-y-2">
				{positions.map((position) => {
					const example = template.parameterExamples?.[Number(position) - 1]
					return (
						<div key={position} className="flex items-center gap-3">
							<label
								htmlFor={`autofill-${template.id}-${position}`}
								className="text-sm font-medium text-gray-700 w-16"
							>
								{`{{${position}}}`}
							</label>
							<Select
								value={draft[position] ?? NO_AUTOFILL_VALUE}
								onValueChange={(value) => setDraft((prev) => ({ ...prev, [position]: value }))}
								disabled={isSaving}
							>
								<SelectTrigger
									id={`autofill-${template.id}-${position}`}
									className="flex-1"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_AUTOFILL_VALUE}>
										{t('whatsappSettings.autofillNoneOption')}
									</SelectItem>
									{template.allowedParameterFields.map((field) => (
										<SelectItem key={field} value={field}>
											{t(`whatsappSettings.autofillField.${field.replace('.', '_')}`, {
												defaultValue: field,
											})}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{example && (
								<span className="text-xs text-gray-400 truncate max-w-[160px]">
									{t('whatsappSettings.autofillExample', { example })}
								</span>
							)}
						</div>
					)
				})}
			</div>

			<div className="flex items-center justify-end gap-2">
				<Button type="subtle" size="short" onClick={onCancel} disabled={isSaving}>
					<span className="flex items-center gap-1.5">
						<XIcon className="size-4" />
						{t('common.cancel')}
					</span>
				</Button>
				<Button type="committal" size="short" onClick={handleSave} isLoading={isSaving}>
					<span className="flex items-center gap-1.5">
						<CheckIcon className="size-4" />
						{t('common.save')}
					</span>
				</Button>
			</div>
		</div>
	)
}
